// @ts-strict-ignore
// @ts-check
import isNumber from 'lodash/isNumber';
import {default as pluralizeExternal} from 'pluralize';

import {fuzzyMatch} from './fuzzy-match';

/**
 * Convert the first letter of the FIRST word of a string to uppercase.
 * By default, leaves all other letters as-is. Pass {strict: true} as second arg to lowercase other letters.
 *
 * @example
 * // returns "Here's an EXAMPLE"
 * capitalize(`here's an EXAMPLE`);
 *
 * @example
 * // returns "Here's an example"
 * capitalize(`here's an EXAMPLE`, {strict: true});
 *
 * @param str - string to capitalize
 * @param [options]
 * @param [options.strict=false] - if true, convert all letters OTHER THAN first to lowercase
 * @returns string with the first letter capitalized (and optionally, the rest lowercased)
 */
export function capitalize(str: string, {strict = false}: {strict?: boolean} = {}): string {
  const rest = strict ? str.slice(1).toLowerCase() : str.slice(1);
  return str && str.charAt(0).toUpperCase() + rest;
}

/**
 * Convert the first letter of the EVERY word in a string to uppercase.
 * By default, converts all other letters to lowercase. Pass {strict: false} as second arg to lowercase other letters.
 *
 * @example
 * // returns "Here's An Example"
 * toTitleCase(`here's an EXAMPLE`);
 *
 * @example
 * // returns "Here's An EXAMPLE"
 * toTitleCase(`here's an EXAMPLE`, {strict: false});
 *
 * @param str - string to convert to title case
 * @param [options]
 * @param [options.strict=true] - if true, convert all letters OTHER THAN first of each word to lowercase
 * @returns string with the first letter of each word capitalized
 */
export function toTitleCase(str: string, {strict = true}: {strict?: boolean} = {}): string {
  return (
    str &&
    str
      .split(` `)
      .map((substr) => capitalize(substr, {strict}))
      .join(` `)
  );
}

/**
 * Convert the first letter of the FIRST word of a string to uppercase.
 * By default, converts all other letters to lowercase. Pass {strict: false} as second arg to lowercase other letters.
 *
 * @example
 * // returns "Here's an example"
 * toTitleCase(`here's an EXAMPLE`);
 *
 * @example
 * // returns "Here's an EXAMPLE"
 * toTitleCase(`here's an EXAMPLE`, {strict: false});
 *
 * @param {string} str - string to convert to title case
 * @param {object} [options]
 * @param {boolean} [options.strict=true] - if true, convert all letters OTHER THAN first to lowercase
 * @returns {string} string with the first letter capitalized
 */
export function toSentenceCase(str: string, {strict = true}: {strict?: boolean} = {}): string {
  return capitalize(str, {strict});
}

/**
 * Convert a string to its plural form based on a given numeric value or boolean switch.
 *
 * singular: numItems is exactly 1 (or if not number, numItems is truthy)
 * plural: >1, 0, 0.5, -1, Infinity, NaN - anything that isn't 1
 *
 * @example
 * // returns "nyan cats"
 * pluralize(`nyan cat`, 10000);
 *
 * @example
 * // returns "nyan cat properties"
 * pluralize(`nyan cat property`, 10000, `nyan cat properties`);
 *
 * @example
 * // returns "nyan cats"
 * pluralize(`nyan cat`, 0.5);
 *
 * @param singular - string to pluralize in its singular form
 * @param numItems - number of items being labled, or switch to force plural or singular
 * @param [plural] - optional plural form of string (if omitted, <singular> + "s" will be used)
 * @param [pluralSuffix] - DEPRECATED - optional plural form of string (if omitted, <singular> + "s" will be used)
 * @returns singular or plural string (based on numItems arg)
 */
export function pluralize(
  singular: string,
  numItems: number | boolean,
  plural: string | null = null,
  pluralSuffix = null,
): string {
  if (numItems === false) {
    return singular;
  } else if (!plural && !pluralSuffix) {
    return pluralizeExternal(singular, numItems);
  }
  const isPlural = isNumber(numItems) ? numItems !== 1 : Boolean(numItems);
  return !isPlural ? singular : plural || singular + pluralSuffix;
}

/**
 * Cut off end of string, replacing last 3 chars with ellipses.
 * If maxChars <= 3, will simply truncate string to maxChars without ellipses.
 *
 * @param str - string to truncate
 * @param maxChars - number of characters to truncate the string to
 * @returns truncated string exactly maxChars long (including ellipses)
 */
export function truncate(str: string, maxChars: number): string {
  if (!str || str.length <= maxChars) {
    return str;
  }
  if (maxChars <= 3) {
    return [...str].slice(0, maxChars).join(``);
  }
  return [...str].slice(0, maxChars - 3).join(``) + `...`;
}

/**
 * Cut out the middle of string, replacing it with ellipses.
 * If maxChars <= 3, will simply truncate string to maxChars without ellipses.
 *
 * @param str - string to truncate
 * @param maxChars - number of characters to truncate the string to
 * @returns truncated string exactly maxChars long (including ellipses)
 */
export function truncateMiddle(str: string | undefined, maxChars: number): string {
  if (!str || str.length <= maxChars) {
    return str;
  }
  if (maxChars <= 3) {
    return str.slice(0, maxChars);
  }
  const start = Math.ceil((maxChars - 3) / 2);
  const end = -1 * Math.floor((maxChars - 3) / 2);
  return str.slice(0, start) + `...` + (end ? str.slice(end) : ``);
}

/**
 * Convert a full name to a maximum of 2 capitalized initials.
 *
 * @example
 * // returns "DV"
 * nameToInitials(`Darth Thelonius Vader`);
 *
 * @param name - full name to convert
 * @returns 0-2 capitalized initials from name
 */
export function nameToInitials(name: string): string {
  if (!name) {
    return ``;
  }

  // return a maximum of 2 initials for a user
  const initials = name
    .split(` `)
    .map((s) => s[0])
    .join(``)
    .toUpperCase();
  return initials.length > 2 ? initials[0] + initials.slice(-1) : initials;
}

/**
 * Split a string into match and non-match substrings based on finding all
 * whitespace-separated terms in a given filter string (for UI search bars).
 *
 * @example
 * // returns ["my", " ", "ex", "ample ", "str", "ing"]
 * stringFilterMatches(`my example string`, `myexstr`);
 *
 * @param targetStr - string to match on (haystack)
 * @param filterStr - search filter string (needle)
 * @returns list of alternating matching and non-matching substrings in order of the original string; even = match, odd = no match
 */
export function stringFilterMatches(targetStr: string, filterStr: string): string[] | null {
  const match = fuzzyMatch(targetStr, filterStr);
  return match && match.matchParts;
}

/**
 * Return the number of symbols in a string, with proper handling for strings that contain unicode characters.
 * See https://mathiasbynens.be/notes/javascript-unicode
 *
 * @example
 * // returns 1
 * countSymbols('👍');
 *
 * @param str - string whose number of symbols is to be returned
 * @returns the length of the string, or zero if the string is null or undefined
 */
export function countSymbols(str: string): number {
  if (!str) {
    return 0;
  }

  return [...str].length;
}

/**
 * Returns a formatted phone number string given a country code and phone number, option to mask last 4 digits.
 * (Ported from mp.security)
 *
 * @example
 * // returns "+1 123-456-7890"
 * formatPhoneNumber(`1`, `1234567890`, false);
 *
 * @param countryCode - country code portion of a phone number
 * @param localPhoneNumber - portion of a phone number after the country code
 * @param mask - option to mask last 4 digits, default is true
 * @returns formatted phone number string
 */
export function formatPhoneNumber(countryCode: string, localPhoneNumber: string, mask = true): string {
  const strippedCountryCode = countryCode.replace(/\D/g, ``);
  const strippedPhoneNumber = localPhoneNumber.replace(/\D/g, ``);
  let phoneNumber;
  let errorMsg;

  if (strippedCountryCode === `1`) {
    if (strippedPhoneNumber.length !== 10) {
      errorMsg = `North American phone numbers (country code 1) must have 10 digits`;
    } else {
      phoneNumber = `+${strippedCountryCode} ${strippedPhoneNumber.slice(0, 3)}-${strippedPhoneNumber.slice(
        3,
        6,
      )}-${strippedPhoneNumber.slice(6, 10)}`;
    }
  } else {
    if (!(strippedCountryCode && strippedPhoneNumber && strippedCountryCode.length < 4)) {
      errorMsg = `Number and country code required`;
    } else {
      phoneNumber = `+${strippedCountryCode} ${strippedPhoneNumber}`;
    }
  }

  if (errorMsg) {
    throw new Error(errorMsg);
  } else {
    if (mask) {
      phoneNumber = phoneNumber.slice(0, -4) + `****`;
    }
    return phoneNumber;
  }
}

const VALID_PHONE_FILTER = /\+(\d{1,3})([-. (])(\(?\d+)/;

/**
 * Returns an object with countryCode and localPhoneNumber properties from a valid, formatted phone number string @see isValidFormattedPhoneNumber.
 *
 * @example
 * // returns {countryCode: "1", localPhoneNumber: "123-456-7890"}
 * parsePhoneNumber(`+1 123-456-7890`);
 *
 * @param str - formatted phone number string
 * @returns {{countryCode: string, localPhoneNumber: string}}
 */
export function parsePhoneNumber(str: string): {countryCode: string; localPhoneNumber: string} {
  const results = VALID_PHONE_FILTER.exec(str);
  if (results) {
    const [, countryCode, separator] = results;
    let localPhoneNumber = str.split(separator).slice(1).join(separator);
    if (separator === `(`) {
      localPhoneNumber = separator + localPhoneNumber;
    }
    return {countryCode, localPhoneNumber};
  }
  throw new Error(`Invalid phone format, must include + and a valid separator after the country code`);
}

// See https://stackoverflow.com/questions/46155/how-can-you-validate-an-email-address-in-javascript
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

/**
 * Return boolean indicating whether a given email string is a valid email address. Case insensitive.
 *
 * @param email - the email to validate
 * @returns boolean indicating whether the given email is valid
 */
export function isValidEmailAddress(email: string): boolean {
  return emailRegex.test(email);
}

/**
 * Check whether a given string looks like a valid domain (very basic for now)
 * @param {string} str - string to check
 * @returns {boolean} whether string passes domain test
 */
const DOMAIN_FILTER = /^[^ ]+\.[^ ]+$/;
export function isValidDomain(str: string) {
  return DOMAIN_FILTER.test(str.trim());
}

/**
 * Remove all non-alpha/unicode characters
 * @param {string} str - string to sanitize
 * @returns {string} the sanitized string
 */
export function alphaUnicodeOnly(str: string) {
  return (str.match(new RegExp(`[a-z0-9]|[^\\u0000-\\u007F]`, `gi`)) ?? []).join(``);
}

/**
 * Return boolean indicating whether a given email has a "verified" domain per provided list.
 *
 * @param email - the email to verify
 * @param domains - array of verified domains (must be passed as {domain: <string>} objects)
 * @returns boolean indicating whether the given email is verified
 */
export function isVerifiedDomain(email: string, domains: Array<{domain: string}>): boolean {
  let isVerified = true;
  // the org has verified domains
  if (domains && domains.length && email.includes(`@`)) {
    isVerified = false;
    const emailDomain = email.split(`@`)[1];
    isVerified = domains.some((domainRow) => domainRow.domain === emailDomain);
  }
  return isVerified;
}

/**
 * Turn a list of strings into `A, B, C`  or `A, B + X more` format depending on the length of the strings.
 *
 * @param items - array of strings to turn into the output string
 * @param fitToLength - the maximum length of the returnStr
 * @returns the formatted string
 */
export function abbreviateStrings(items: string[], fitToLength: number): string {
  let returnStr;
  if (items.length === 0) {
    returnStr = ``;
  } else {
    const commaSeparated = items.join(`, `);
    if (commaSeparated && commaSeparated.length <= fitToLength) {
      returnStr = commaSeparated;
    } else {
      let curLen = `${items[0]} + ${items.length} more`.length; // Saves some space for the + X more
      const shownItems = [items[0]];
      for (let i = 1; i < items.length; ++i) {
        curLen += `, ${items[i]}`.length;
        if (curLen <= fitToLength) {
          // add an item if it will fit
          shownItems.push(items[i]);
        } else {
          break;
        }
      }
      const andMoreStr = ` + ${items.length - shownItems.length} more`; // Can't be `+ 0 more` in the else block
      returnStr = truncateMiddle(shownItems.join(`, `), fitToLength - andMoreStr.length) + andMoreStr;
    }
  }
  return returnStr;
}

/**
 * Turn a list of strings into a compound sentence separated by commas and a conjunction at the end.
 *
 * @example
 * // returns "dog or cat"
 * compoundSentence([`dog`, `cat`], `or`)
 *
 * @example
 * // returns "cool, great, awesome and I love it"
 * compoundSentence([`cool`, `great`, awesome`, `I love it`], `and`)
 *
 * @param strings - array of statements to combine
 * @param conjunction - "and" or "or"
 * @returns the formatted string
 */
export function compoundSentence(strs: string[], conjunction: `and` | `or` | `&` = `and`): string {
  const leadingSubjects = strs.slice(0, -2);
  return leadingSubjects.join(`, `) + (leadingSubjects.length ? `, ` : ``) + strs.slice(-2).join(` ${conjunction} `);
}

export async function sha256(str: string, cryptoLib: Crypto = crypto): Promise<string> {
  // implementation from https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
  const msgUint8 = new TextEncoder().encode(str); // encode as (utf-8) Uint8Array
  const hashBuffer = await cryptoLib.subtle.digest(`SHA-256`, msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, `0`)).join(``); // convert bytes to hex string
  return hashHex;
}
